import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { ArrowRight } from 'lucide-react';

import type { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/public-filter-constants';
import type { LogicalOperator } from '@/entities/query/query-types';
import { socialApiClient } from '@/shared/api-client/social-api-client';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { showDialog } from '@/shared/utils/show-dialog';
import { showErrorDialog } from '@/shared/utils/show-error-dialog';

async function createQuery({
  workspaceId,
  name,
  platforms,
}: {
  workspaceId: string;
  name: string;
  platforms: SOURCE_PLATFORM_TYPE[];
}) {
  const response = await socialApiClient.post<{
    id: string;
    type: string;
    name: string;
    filter: {
      exclude: {
        op: number;
        values: string[] | null;
      };
      include: {
        op: number;
        values: string[] | null;
      };
    };
  }>(`workspaces/${workspaceId}/queries`, {
    json: {
      name,
      platforms,
    },
  });
  const data = await response.json();

  // Map to SavedQuery type
  return {
    id: data.id,
    type: data.type as 'private' | 'public',
    name: data.name,
    filter: {
      exclude: {
        op: data.filter.exclude.op as LogicalOperator,
        values: data.filter.exclude.values || [],
      },
      include: {
        op: data.filter.include.op as LogicalOperator,
        values: data.filter.include.values || [],
      },
    },
  };
}

export function useCreateQuery() {
  const { workspaceId } = useWorkspaceId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: createQuery,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['saved-queries', variables.workspaceId],
      });

      navigate({
        to: '/workspace/$workspaceId/listening',
        params: { workspaceId: variables.workspaceId },
      });
    },
    onError: async (error) => {
      // Check if it's a query limit error (code 107)
      if (error instanceof HTTPError) {
        try {
          const errorData = (await error.response.json()) as {
            code: number;
            data: null;
            message: string;
          };

          if (errorData.code === 107) {
            showDialog({
              title: 'Query Limit Reached',
              message:
                "You've reached the maximum number of queries allowed for your workspace. Please upgrade your plan to create more queries.",
              onConfirm: () => {
                navigate({
                  to: '/workspace/$workspaceId/settings/plan',
                  params: {
                    workspaceId,
                  },
                });
              },
              confirmLabel: (
                <>
                  Upgrade plan
                  <ArrowRight className="size-4" />
                </>
              ),
            });
            return;
          }
        } catch {
          // If we can't parse the error, fall through to default handling
        }
      }

      // Default error handling
      showErrorDialog({
        error,
        title: 'Failed to create query',
      });
    },
  });
}
